// App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';

// Components
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import UserDashboard from './pages/UserDashboard';
import Callback from './pages/Callback';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import VirtualRaces from './virtual_race/pages/VirtualRaces';
import RaceDetails from './virtual_race/pages/RaceDetails';
import VirtualRaceSuccess from './virtual_race/pages/VirtualRaceSuccess';

// Check if origin is secure (localhost or HTTPS)
const isSecureOrigin = () => {
  const origin = window.location.origin;
  return origin.startsWith('https://') || 
         origin.startsWith('http://localhost') || 
         origin.startsWith('http://127.0.0.1');
};

// Custom component to handle the Auth0 redirect
const Auth0ProviderWithRedirectCallback = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Public routes that don't need Auth0 and can work without it
  // These routes don't use any Auth0 hooks
  const publicRoutesWithoutAuth = ['/payment', '/payment/success', '/payment/cancel', '/privacy-policy'];
  const isPublicRouteWithoutAuth = publicRoutesWithoutAuth.some(route => location.pathname.startsWith(route));
  
  // Only initialize Auth0 if:
  // 1. We're on a secure origin (localhost or HTTPS), OR
  // 2. We're NOT on a public route that doesn't need Auth0
  // This allows payment pages to work on insecure origins (like local IP addresses)
  const shouldUseAuth0 = isSecureOrigin() || !isPublicRouteWithoutAuth;
  
  const onRedirectCallback = (appState) => {
    console.log("Auth0 redirect callback triggered with appState:", appState);
    navigate(appState?.returnTo || '/profile');
  };

  // If we shouldn't use Auth0 (insecure origin + public route that doesn't need it), skip Auth0Provider
  // This prevents the "auth0-spa-js must run on a secure origin" error for payment pages
  if (!shouldUseAuth0) {
    console.log('Skipping Auth0Provider for public route on insecure origin:', location.pathname);
    return <>{children}</>;
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        scope: "openid profile email",
        audience: process.env.REACT_APP_AUTH0_AUDIENCE
      }}
      useRefreshTokens={false}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

const AppLayout = () => {
  const location = useLocation();
  
  // Check if virtual race feature is enabled
  const isVirtualRaceEnabled = process.env.REACT_APP_VIRTUAL_RACE === 'true';

  return (
    <div className="app-container">
      <Navigation />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/hogyan-mukodik" element={<HowItWorks />} />
          <Route path="/callback" element={<Callback />} />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            } 
          />
          {isVirtualRaceEnabled && (
            <>
              <Route path="/verseny" element={<VirtualRaces />} />
              <Route 
                path="/verseny/:raceId" 
                element={
                  <ProtectedRoute>
                    <RaceDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/verseny/:raceId/success" 
                element={
                  <ProtectedRoute>
                    <VirtualRaceSuccess />
                  </ProtectedRoute>
                } 
              />
            </>
          )}
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/delete-account" element={<ProtectedRoute><DeleteAccount /></ProtectedRoute>} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Auth0ProviderWithRedirectCallback>
        <AppLayout />
      </Auth0ProviderWithRedirectCallback>
    </Router>
  );
};

export default App;